import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import SearchableSelect from './SearchableSelect';
import { useExternalSourceCatalog } from '../hooks/useExternalSourceCatalog';
import { getExternalEventDisplayTitle } from '../utils/dates';
import '../styles/external-source-picker.css';

const SOURCE_OPTIONS = [
  { key: 'WordPress', label: 'WordPress' },
  { key: 'Faicon', label: 'Faicon' },
];

const getSessionRecordId = (record, source) => {
  if (!record) return '';
  if (source === 'WordPress') {
    return String(record.ID ?? record.id ?? '');
  }
  return String(record.id ?? record.ID ?? record.session_id ?? record.uuid ?? '');
};

const ExternalSourcePicker = forwardRef(function ExternalSourcePicker(
  {
    mode = 'session',
    source,
    onSourceChange,
    onSelectionChange,
    onLoadingChange,
    linkedEventId = '',
    linkedSessionId = '',
    disabled = false,
    showSourceToggle = true,
    showSessionFields = true,
    compact = false,
  },
  ref
) {
  const {
    events,
    days,
    sessions,
    selectedEvent,
    setSelectedEvent,
    selectedDay,
    setSelectedDay,
    selectedSession,
    setSelectedSession,
    loadingEvents,
    loadingDays,
    error,
    loadEvents,
  } = useExternalSourceCatalog(source, { linkedEventId, linkedSessionId });

  const sessionLabelKey = source === 'WordPress' ? 'post_title' : 'name';
  const sessionValueKey = source === 'WordPress' ? 'ID' : 'id';

  const buildSelection = (nextEvent, nextSession) => {
    const eventId = nextEvent?.id ? String(nextEvent.id) : '';
    const sessionId = getSessionRecordId(nextSession, source);
    const sessionRecord = nextSession || null;

    return {
      source,
      eventId,
      sessionId,
      eventTitle: getExternalEventDisplayTitle(nextEvent),
      sessionTitle: sessionRecord
        ? String(sessionRecord[sessionLabelKey] || sessionRecord.title || sessionRecord.name || '')
        : '',
    };
  };

  const emitSelection = (nextEvent, nextDay, nextSession) => {
    if (!onSelectionChange) return;
    onSelectionChange(buildSelection(nextEvent, nextSession));
  };

  const isLoading = loadingEvents || (mode === 'session' && showSessionFields && loadingDays);
  const isReady = !isLoading;
  const showInitialEventsLoading = loadingEvents && events.length === 0;
  const showDaysLoading = mode === 'session' && showSessionFields && selectedEvent && loadingDays && days.length === 0;

  useImperativeHandle(
    ref,
    () => ({
      getSelection: () => ({
        ...buildSelection(selectedEvent, selectedSession),
        isReady,
        isLoading,
        hasEvent: Boolean(selectedEvent),
        hasSession: Boolean(selectedSession),
      }),
    }),
  );

  useEffect(() => {
    if (!onLoadingChange) return;
    if (!isLoading) {
      onLoadingChange(false);
      return undefined;
    }
    const timer = window.setTimeout(() => onLoadingChange(true), 280);
    return () => window.clearTimeout(timer);
  }, [isLoading, onLoadingChange]);

  const handleEventChange = (event) => {
    const nextEvent = event && typeof event === 'object' ? event : null;
    setSelectedEvent(nextEvent);
    setSelectedDay(null);
    setSelectedSession(null);
    emitSelection(nextEvent, null, null);
  };

  const handleDayChange = (day) => {
    const nextDay = day && typeof day === 'object' ? day : null;
    setSelectedDay(nextDay);
    setSelectedSession(null);
    emitSelection(selectedEvent, nextDay, null);
  };

  const handleSessionChange = (sessionOption) => {
    const nextSession = sessionOption && typeof sessionOption === 'object' ? sessionOption : null;
    setSelectedSession(nextSession);
    emitSelection(selectedEvent, selectedDay, nextSession);
  };

  const showSessionPickers = mode === 'session' && showSessionFields && selectedEvent;
  const showEventField = events.length > 0 || showInitialEventsLoading;

  return (
    <div className={`external-source-picker ${compact ? 'compact' : ''} ${isLoading ? 'is-loading' : ''}`}>
      {showSourceToggle && (
        <div className="external-source-picker-sources">
          {SOURCE_OPTIONS.map((option) => {
            const active = source === option.key;
            return (
              <button
                key={option.key}
                type="button"
                className={`external-source-picker-source ${active ? 'active' : ''}`}
                onClick={() => onSourceChange?.(option.key)}
                disabled={disabled}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      {error && (
        <div className="external-source-picker-error">
          <span>{error}</span>
          <button type="button" onClick={loadEvents} disabled={disabled || loadingEvents}>
            Retry
          </button>
        </div>
      )}

      {showEventField && (
        <label className="external-source-picker-field">
          <span className="external-source-picker-field-label">
            Event
            {loadingEvents && events.length > 0 && (
              <span className="external-source-picker-inline-status">Updating…</span>
            )}
          </span>
          {showInitialEventsLoading ? (
            <div className="external-source-picker-skeleton" aria-hidden="true" />
          ) : (
            <SearchableSelect
              options={events}
              value={selectedEvent}
              onChange={handleEventChange}
              placeholder="Search and select an event..."
              optionLabel="displayTitle"
              optionValue="id"
              labelLines={2}
              disabled={disabled}
              triggerClassName="external-source-picker-select"
            />
          )}
        </label>
      )}

      {showSessionPickers && (
        <div className="external-source-picker-session-fields">
          {showDaysLoading ? (
            <div className="external-source-picker-skeleton external-source-picker-skeleton-short" aria-hidden="true" />
          ) : (
            <>
              {days.length > 0 && (
                <label className="external-source-picker-field">
                  <span className="external-source-picker-field-label">Day</span>
                  <SearchableSelect
                    options={days}
                    value={selectedDay}
                    onChange={handleDayChange}
                    placeholder="Select a day..."
                    optionLabel="date"
                    optionValue="date"
                    disabled={disabled}
                    triggerClassName="external-source-picker-select"
                  />
                </label>
              )}

              {selectedDay && sessions.length > 0 && (
                <label className="external-source-picker-field">
                  <span className="external-source-picker-field-label">Session</span>
                  <SearchableSelect
                    options={sessions}
                    value={selectedSession}
                    onChange={handleSessionChange}
                    placeholder="Select a session..."
                    optionLabel={sessionLabelKey}
                    optionValue={sessionValueKey}
                    labelLines={2}
                    disabled={disabled}
                    triggerClassName="external-source-picker-select"
                  />
                </label>
              )}

              {days.length === 0 && !error && (
                <p className="external-source-picker-note">No sessions found for this event.</p>
              )}
            </>
          )}

          {loadingDays && days.length > 0 && (
            <p className="external-source-picker-inline-status block">Refreshing sessions…</p>
          )}
        </div>
      )}

    </div>
  );
});

export default ExternalSourcePicker;
